import { inject, observer } from "mobx-react";
import React, { useEffect, useRef, useState } from "react";
import { BlockJson, CategoryJsonModel, ModeJsonModel } from "../models/BlockTransportSystemJsonModel";
import BlockKitStore from "../stores/BlockKitStore";
import CategoryInfoCard from "../components/CategoryInfoCard/CategoryInfoCard";
import CategoryBlocksChangeModal from "../components/CategoryBlocksChangeModal/CategoryBlocksChangeModal";
import CategoryBlocksPublishModal from "../components/CategoryBlocksPublishModal/CategoryBlocksPublishModal";
import { AllCommunityModule, ICellRendererParams, ModuleRegistry } from 'ag-grid-community';
import { AgGridReact } from 'ag-grid-react';
import GlobalEnvironments from "../constants/GlobalEnvironments";
import { getDateWithoutSeconds, getLocaleString } from "../utils/DateHelper";
import { v4 as uuidv4 } from "uuid";
import { Badge, Button, Container, Spinner, Stack } from "react-bootstrap";
import StudioUserStore from "../stores/StudioUserStore";
import CategoryModeEditModal from "../components/CategoryModeEditModal/CategoryModeEditModal";
import { useNavigate } from "react-router-dom";
import i18n from "../../shared/internationalization/I18nSetting";
import PermissionModal from "../PermissionModal/PermissionModal";
import { useTranslation } from "react-i18next";

ModuleRegistry.registerModules([AllCommunityModule]);

export interface IProps {
  blockKitStore?: BlockKitStore;
  studioUserStore?: StudioUserStore;
}

const CategoryPage: React.FC<IProps> = inject("blockKitStore", "studioUserStore")(observer(({
  blockKitStore,
  studioUserStore }) => {
  const [selectedCategory, setSelectedCategory] = useState<CategoryJsonModel>();
  const [isShowCategoryBlocksChangeModal, setIsShowCategoryBlocksChangeModal] = useState<boolean>(false);
  const [isShowCategoryBlocksPublishModal, setIsShowCategoryBlocksPublishModal] = useState<boolean>(false);
  const [isOpenEditModal, setIsOpenEditModal] = useState<boolean>(false);
  const [isShowCategoryModeEditModal, setIsShowCategoryModeEditModal] = useState<boolean>(false);
  const [isShowCategoryPermissionModal, setIsShowCategoryPermissionModal] = useState<boolean>(false);
  const [isCreateMode, setIsCreateMode] = useState<boolean>(false);
  const [isWorkingPublish, setIsWorkingPublish] = useState<boolean>(false);
  const [isWorkingCopy, setIsWorkingCopy] = useState<boolean>(false);
  const gridRef = useRef<AgGridReact<CategoryJsonModel>>(null);
  const [userModes, setUserModes] = useState<ModeJsonModel[]>([]);
  const { t } = useTranslation();

  useEffect(() => {
    blockKitStore?.initCategoryPageAsync();
    blockKitStore?.studioController.getUserModesAsync().then(setUserModes);
  }, [])

  useEffect(() => {
    gridRef.current?.api?.refreshCells({ force: true });
  }, [blockKitStore?.timeZone]);

  const ImageCell = (props: ICellRendererParams) => {
    const imgSrc = `${GlobalEnvironments.getBlockTransportSystemHostUrlForWeb()}${props.value}`;
    return props.value ? (
      <a href={imgSrc} target="_blank" rel="noopener noreferrer">
        <img
          src={imgSrc}
          alt="thumbnail"
          style={{ width: "100%", height: "100%", objectFit: 'contain' }}
        />
      </a>
    ) : null;
  };

  const EditButtonCell: React.FC<ICellRendererParams> = (params) => {
    return (
      <Button
        variant="secondary"
        size="sm"
        onClick={async () => {
          setSelectedCategory(params.data);
          setIsCreateMode(false);
          setIsOpenEditModal(true);
          params.api.deselectAll();
          params.node.setSelected(true);
        }}>
        {t("button-edit")}
      </Button>
    );
  }

  const ModeButtonCell: React.FC<ICellRendererParams> = (params) => {
    return (
      <Button
        variant="secondary"
        size="sm"
        onClick={async () => {
          setSelectedCategory(params.data);
          setIsShowCategoryModeEditModal(true);
          params.api.deselectAll();
          params.node.setSelected(true);
        }}>
        {`${t("button-mode")}(${blockKitStore?.allExposureCategories.filter(i => i.categoryId === params.data.id)?.length})`}
      </Button>
    );
  }

  const ListViewCell: React.FC<ICellRendererParams<CategoryJsonModel, string[]>> = (params) => {
    return (
      <select style={{ width: "100%" }}>
        <option>{`${t("view-list")}(${params.value?.length})`}</option>
        {params.value?.map((i, index) => <option key={index} disabled>{i}</option>)}
      </select>
    );
  }

  const NameCell: React.FC<ICellRendererParams<CategoryJsonModel>> = (params) => {
    const navigate = useNavigate();
    return (
      <span
        style={{ color: "blue", cursor: "pointer" }}
        onClick={() => navigate(`/category/${params.data?.id}`)}
      >
        {params.data?.lang.find(i => i.key === i18n.language)?.name ?? params.value}
      </span>
    );
  };

  return (
    <Container fluid>
      {isShowCategoryBlocksChangeModal && (
        <CategoryBlocksChangeModal
          category={selectedCategory}
          categories={blockKitStore?.allCategories}
          blocks={blockKitStore?.allBlocks}
          isShow={isShowCategoryBlocksChangeModal}
          onClickApply={async (blocks: BlockJson[], toCategoryId: string) => {
            const category = blockKitStore?.allCategories.find(i => i.id === toCategoryId);
            if (!category) {
              alert(t("category-not-found-message"));
              return;
            }

            for (const block of blocks) {
              block.data.categoryId = category.id;
              block.data.category = category.name;
              await blockKitStore?.studioController.postBlockStagingAsync(block.data);
            }
            await blockKitStore?.initCategoryPageAsync();
          }}
          onClickClose={() => setIsShowCategoryBlocksChangeModal(false)}
        />
      )}
      {isShowCategoryBlocksPublishModal && (
        <CategoryBlocksPublishModal
          category={selectedCategory}
          blocks={blockKitStore?.allBlocks}
          isShow={isShowCategoryBlocksPublishModal}
          onClickPublish={async (categoryId: string, blockType: string, progressEvent: (name: string, count: number, totalCount: number) => void) => {
            const result = await blockKitStore?.publishBlocks(categoryId, blockType, progressEvent);
            await blockKitStore?.initCategoryPageAsync();
            alert(result?.message);
          }}
          onClickClose={() => setIsShowCategoryBlocksPublishModal(false)}
        />
      )}
      {isOpenEditModal &&
        <CategoryInfoCard
          isShow={isOpenEditModal}
          isCreateMode={isCreateMode}
          category={selectedCategory}
          categoryPackages={blockKitStore?.allCategoryPackages}
          timeZone={blockKitStore?.timeZone}
          allowedPresets={studioUserStore?.me?.allowedPresets ?? []}
          onClickApply={async (category: CategoryJsonModel, file?: File) => {
            if (isCreateMode) {
              const result = await blockKitStore?.changeCategoryAsync(category, file);
              if (result) {
                await blockKitStore?.studioController.postCategoriesStagingAsync(result);
              }
            } else {
              const result = await blockKitStore?.changeCategoryAsync(category, file);
              if (result) {
                await blockKitStore?.studioController.putCategoryStagingAsync(result);
              }
            }
            await blockKitStore?.initCategoryPageAsync();
            setIsOpenEditModal(false);
          }}
          onClickClose={() => {
            setIsOpenEditModal(false);
          }}
        />}
      {isShowCategoryModeEditModal &&
        <CategoryModeEditModal
          isShow={isShowCategoryModeEditModal}
          category={selectedCategory}
          exposureCategories={blockKitStore?.allExposureCategories}
          modes={userModes}
          onSave={async (modeIds: string[]) => {
            if (selectedCategory?.id) {
              await blockKitStore?.studioController?.postCategoryModesStaging(selectedCategory?.id, modeIds)
              await blockKitStore?.initCategoryPageAsync();
              setIsShowCategoryModeEditModal(false);
            } else {
              alert(t("category-id-invalid-message"));
            }
          }}
          onClose={() => setIsShowCategoryModeEditModal(false)}
        />}
      <PermissionModal<CategoryJsonModel>
        entityType="categories"
        entity={selectedCategory}
        isShow={isShowCategoryPermissionModal}
        onClose={() => setIsShowCategoryPermissionModal(false)} />
      <Stack direction="horizontal" gap={3} className="m-2">
        <Button
          variant="primary"
          onClick={() => {
            setSelectedCategory({
              id: uuidv4(),
              name: "",
              lang: [],
              dedicatedProducts: [],
              description: "",
              priority: 3,
              blockCount: 0,
              releaseDate: getDateWithoutSeconds(),
              expireDate: new Date(2100, 1, 1)
            });
            setIsCreateMode(true);
            setIsOpenEditModal(true);
          }}>
          {t("button-new")}
        </Button>
        <Button
          onClick={async () => {
            setIsWorkingCopy(true);
            try {
              const result = await blockKitStore?.copyCategory(selectedCategory!);
              if (result) {
                const exposreCategories = await blockKitStore?.studioController.getExposureCategoriesStagingAsync(selectedCategory!);
                if (exposreCategories) {
                  await blockKitStore?.studioController.postCategoriesStagingAsync(result);
                  for (const cat of exposreCategories) {
                    await blockKitStore?.studioController.postExposureCategoryStagingAsync(cat.modeId, result.id)
                  }
                }
                await blockKitStore?.initCategoryPageAsync();
              }
            } finally {
              setIsWorkingCopy(false);
            }
          }}
          disabled={!selectedCategory || isWorkingCopy}
        >
          {t("button-copy")}
        </Button>
        <Button
          onClick={async () => {
            setIsWorkingPublish(true);
            try {
              if (!confirm(`${selectedCategory?.name} ${t("publish-confirmation-message")}`)) {
                return;
              }

              const response = await blockKitStore?.studioController.publishCategoryStagingAsync(selectedCategory);
              if (response?.isSuccess) {
                alert(t("publish-success"));
              } else {
                alert(`${t("publish-failed")} (${response?.message})`);
              }
              await blockKitStore?.initCategoryPageAsync();
            } finally {
              setIsWorkingPublish(false);
            }
          }}
          disabled={!selectedCategory || isWorkingPublish}
        >
          {isWorkingPublish &&
            <Spinner
              className="me-1"
              animation="border"
              size="sm"
              role="status"
            />}
          {t("button-publish")}
        </Button>
        <Button
          onClick={() => {
            setIsShowCategoryBlocksChangeModal(true);
          }}
          disabled={!selectedCategory}
        >
          {t("button-change-designs-category")}
        </Button>
        <Button
          onClick={() => {
            setIsShowCategoryBlocksPublishModal(true);
          }}
          disabled={!selectedCategory}
        >
          {t("button-publish-category-designs")}
        </Button>
        <Button onClick={() => setIsShowCategoryModeEditModal(true)} disabled={!selectedCategory}>{t("button-mode")}</Button>
        <Button
          variant="danger"
          onClick={async () => {
            if (!confirm(`${selectedCategory?.name} ${t("delete-confirmation-message")}\n${t("category-designs-delete-warning")}`)) {
              return;
            }

            const response = await blockKitStore?.studioController.deleteCategoryStagingAsync(selectedCategory!);
            if (response.isSuccess) {
              await blockKitStore?.initCategoryPageAsync();
            }

            alert(response.message);
          }}
          disabled={!selectedCategory || isWorkingPublish}
        >
          {t("button-delete")}
        </Button>
        <Button
          hidden={!studioUserStore?.hasPermission("category:permission")}
          disabled={!selectedCategory}
          variant="warning"
          onClick={() => setIsShowCategoryPermissionModal(true)}
        >
          {t("button-permission")}
        </Button>
      </Stack>
      <div style={{ height: "70vh" }}>
        <AgGridReact<CategoryJsonModel>
          ref={gridRef}
          getRowId={params => params.data.id}
          rowData={blockKitStore?.allCategories}
          columnDefs={[
            {
              headerName: t("publish"),
              width: 80,
              cellRenderer: (params: ICellRendererParams) => {
                return blockKitStore?.publishedCategories.some(i => i.id === params.data?.id)
                  ? <Badge bg="info">{t("published")}</Badge>
                  : null
              },
            },
            {
              headerName: t("edit"),
              width: 80,
              cellRenderer: EditButtonCell,
            },
            {
              headerName: t("thumbnail"),
              field: "thumbnailUrl",
              width: 90,
              cellRenderer: ImageCell,
            },
            {
              headerName: t("id"),
              filter: true,
              field: "id"
            },
            {
              headerName: t("name"),
              filter: true,
              field: "name",
              cellRenderer: NameCell
            },
            {
              headerName: t("description"),
              field: "description",
              filter: true,
            },
            {
              headerName: t("price"),
              field: "price",
              width: 120,
              filter: true,
            },
            {
              headerName: t("dedicated-product"),
              field: "dedicatedProducts",
              width: 180,
              autoHeight: true,
              filter: true,
              cellStyle: {
                "whiteSpaceCollapse": "preserve-breaks",
                "lineHeight": 1.7
              },
              valueGetter: (params) => {
                const arr = params?.data?.dedicatedProducts;
                return Array.isArray(arr) ? arr.sort().join("\n") : arr;
              }
            },
            {
              headerName: t("mode"),
              cellRenderer: ModeButtonCell
            },
            {
              headerName: t("priority"),
              filter: true,
              field: "priority",
              width: 100
            },
            {
              headerName: t("count"),
              filter: true,
              field: "blockCount",
              width: 80
            },
            {
              headerName: t("block-type"),
              autoHeight: true,
              filter: true,
              cellStyle: {
                "whiteSpaceCollapse": "preserve-breaks",
                "lineHeight": 1.7
              },
              valueGetter: (params) => {
                const blocks = blockKitStore?.allBlocks
                const categoryBlocks = blocks?.filter((block) => block?.data?.categoryId === params?.data?.id);
                const types = categoryBlocks.map(b => b.data.type);
                const typeCount = types.reduce<Record<string, number>>((acc, cur) => {
                  acc[cur] = (acc[cur] || 0) + 1;
                  return acc;
                }, {});
                return Object.entries(typeCount).map(([key, value]) => `${key}(${value})`).join("\n");
              }
            },
            {
              headerName: t("release-date"),
              field: "releaseDate",
              filter: "agDateColumnFilter",
              valueFormatter: parmas => getLocaleString(new Date(parmas.data.releaseDate), blockKitStore.timeZone),
            },
            {
              headerName: t("expire-date"),
              field: "expireDate",
              filter: "agDateColumnFilter",
              valueFormatter: params => getLocaleString(new Date(params.data.expireDate), blockKitStore.timeZone)
            },
            {
              headerName: t("name-by-country"),
              autoHeight: true,
              filter: true,
              field: "lang",
              cellRenderer: ListViewCell,
              valueGetter: params => params.data.lang
                ?.filter(l => l.name)
                ?.map(l => `${l.key}( ${l.name} )`)
            },
            {
              headerName: t("probability"),
              filter: true,
              valueGetter: params => params.data.probabilityTables && params.data.probabilityTables.length > 0
                ? "random"
                : ""
            }
          ]}
          rowSelection={{
            mode: "singleRow",
            enableClickSelection: false,
          }}
          onSelectionChanged={(e) => {
            const selected = e.api.getSelectedRows();
            if (selected.length > 0) {
              setSelectedCategory(selected[0]);
            } else {
              setSelectedCategory(null);
            }
          }}
          getRowStyle={params => {
            const expireDate = new Date(params.data.expireDate);
            const now = new Date();
            if (expireDate < now) {
              return { backgroundColor: "#ffe0e0" };
            }
            return null;
          }
          }
        />
      </div>
    </Container>
  )
}));

export default CategoryPage;